import React, { useState } from 'react';
import { Eye, EyeOff, Key, Info, ChevronDown, ChevronRight } from 'lucide-react';
import { Modal } from './ui/Modal';
import { Button } from './ui/Button';
import { Input } from './ui/Input';
import { useAuth } from '../hooks/useAuth';
import { toast } from 'react-hot-toast';

interface SignInModalProps {
  isOpen: boolean;
  onClose: () => void;
  onOpenSignUp?: () => void;
}

export const SignInModal: React.FC<SignInModalProps> = ({ isOpen, onClose, onOpenSignUp }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isPasskeyLoading, setIsPasskeyLoading] = useState(false);
  const [showPasskeyInfo, setShowPasskeyInfo] = useState(false);
  const { signIn, signInWithPasskey } = useAuth();

  const handleEmailSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error('Please fill in all fields');
      return;
    }

    setIsLoading(true);
    try {
      await signIn(email, password);
      toast.success('Signed in successfully!');
      onClose();
      setEmail('');
      setPassword('');
    } catch (error: any) {
      toast.error(error.message || 'Sign in failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasskeySignIn = async () => {
    setIsPasskeyLoading(true);
    try {
      await signInWithPasskey();
      toast.success('Signed in with passkey!');
      onClose();
    } catch (error: any) {
      toast.error(error.message || 'Passkey sign in failed');
    } finally {
      setIsPasskeyLoading(false);
    }
  };

  const handleClose = () => {
    setEmail('');
    setPassword('');
    setShowPassword(false);
    setShowPasskeyInfo(false);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Sign In" size="sm">
      <div className="space-y-6">
        {/* Passkey Sign In */}
        <div className="text-center">
          <Button
            onClick={handlePasskeySignIn}
            disabled={isPasskeyLoading}
            className="w-full flex items-center justify-center space-x-2"
          >
            <Key className="h-4 w-4" />
            <span>{isPasskeyLoading ? 'Signing in...' : 'Sign in with Passkey'}</span>
          </Button>
          
          {/* Collapsible Passkey Explanation */}
          <div className="mt-2">
            <div className="flex justify-center">
              <button
                onClick={() => setShowPasskeyInfo(!showPasskeyInfo)}
                className="flex items-center justify-center space-x-1 text-sm font-semibold text-primary-600 hover:text-primary-700 transition-colors"
              >
                <span>What is a passkey?</span>
                {showPasskeyInfo ? (
                  <ChevronDown className="h-3 w-3" />
                ) : (
                  <ChevronRight className="h-3 w-3" />
                )}
              </button>
            </div>
            
            {showPasskeyInfo && (
              <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg text-left">
                <div className="flex items-start space-x-2">
                  <Info className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <h4 className="text-sm font-medium text-blue-900 mb-1">What is a passkey?</h4>
                    <p className="text-xs text-blue-700">
                      A passkey is a secure way to sign in using your device's built-in security features like Face ID, Touch ID, or Windows Hello. 
                      It's faster and more secure than passwords - no need to remember or type anything!
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Divider */}
        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-300" />
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-2 bg-white text-gray-500">or</span>
          </div>
        </div>

        {/* Email/Password Sign In */}
        <form onSubmit={handleEmailSignIn} className="space-y-4">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
              Email
            </label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
              placeholder="Enter your email"
              required
            />
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
              Password
            </label>
            <div className="relative">
              <Input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)}
                placeholder="Enter your password"
                required
              />
              <button
                type="button"
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? (
                  <EyeOff className="h-4 w-4 text-gray-400" />
                ) : (
                  <Eye className="h-4 w-4 text-gray-400" />
                )}
              </button>
            </div>
          </div>

          <Button
            type="submit"
            disabled={isLoading}
            className="w-full"
          >
            {isLoading ? 'Signing in...' : 'Sign In'}
          </Button>
        </form>

        {/* Sign Up Link */}
        <div className="text-center">
          <p className="text-sm text-gray-600">
            Don't have an account?{' '}
            <button
              onClick={() => {
                onClose();
                onOpenSignUp?.();
              }}
              className="text-primary-600 hover:text-primary-700 font-medium"
            >
              Get Started
            </button>
          </p>
        </div>
      </div>
    </Modal>
  );
};
